import { QueryStatus } from '@reduxjs/toolkit/query';
import { SERIAL_NUMBER_REGEX } from '@rla/constants';
import { selectValidateSerialNumber } from '@rla/services';
import { api } from '@services/api';
import { store } from '@store/store';
import { createDebouncedGate } from '@utils/debouncedGate';
import { IntlShape } from 'react-intl';
import * as Yup from 'yup';

const serialValidationDebounce = createDebouncedGate();
let latestSerialNumber: string | undefined;

export const RLAPinPukSchema = ({ formatMessage }: IntlShape) =>
    Yup.object({
        first_name: Yup.string().trim().required(
            formatMessage(
                {
                    id: 'schema.errors.required',
                    defaultMessage: '{field} is required',
                },
                { field: formatMessage({ id: 'common.firstName', defaultMessage: 'First name' }) }
            )
        ),
        last_name: Yup.string().trim().required(
            formatMessage(
                {
                    id: 'schema.errors.required',
                    defaultMessage: '{field} is required',
                },
                { field: formatMessage({ id: 'common.lastName', defaultMessage: 'Last name' }) }
            )
        ),
        email: Yup.string()
            .trim()
            .required(
                formatMessage(
                    {
                        id: 'schema.errors.required',
                        defaultMessage: '{field} is required',
                    },
                    { field: formatMessage({ id: 'common.email', defaultMessage: 'Email' }) }
                )
            )
            .email(formatMessage({ id: 'schema.errors.email.invalid', defaultMessage: 'Invalid email address' })),
        distributor_name: Yup.string().trim().required(
            formatMessage(
                { id: 'schema.errors.required', defaultMessage: '{field} is required' },
                { field: formatMessage({ id: 'common.distributor', defaultMessage: 'Distributor' }) }
            )
        ),
        product_serial_number: Yup.string()
            .trim()
            .required(
                formatMessage(
                    { id: 'schema.errors.required', defaultMessage: '{field} is required' },
                    { field: formatMessage({ id: 'common.serialNumber', defaultMessage: 'Serial number' }) }
                )
            )
            .test(
                'serial-number-format',
                formatMessage({
                    id: 'schema.errors.serialNumber.invalid',
                    defaultMessage: 'Invalid serial number. Expected format: HQ123456789',
                }),
                (value) => !value || SERIAL_NUMBER_REGEX.test(value)
            )
            .test(
                'check-serial-number',
                formatMessage({
                    id: 'schema.errors.serialNumber.not_found',
                    defaultMessage: 'This serial number is invalid',
                }),
                async (value) => {
                    latestSerialNumber = value;

                    if (!value) {
                        serialValidationDebounce.cancel();
                        return true;
                    }
                    if (!SERIAL_NUMBER_REGEX.test(value)) {
                        serialValidationDebounce.cancel();
                        return true;
                    }

                    const cachedValidation = selectValidateSerialNumber({ serialNumber: value })(store.getState());
                    if (cachedValidation.status === QueryStatus.fulfilled) {
                        return cachedValidation.data?.is_valid_serial_number ?? false;
                    }
                    if (cachedValidation.status === QueryStatus.rejected) {
                        return false;
                    }

                    // Wait for the debounce gate before proceeding with the serial number validation
                    if (
                        cachedValidation.status === QueryStatus.uninitialized &&
                        !(await serialValidationDebounce.wait())
                    ) {
                        return true;
                    }
                    if (latestSerialNumber !== value) return true;

                    try {
                        const result = await store.dispatch(
                            (api as any).endpoints.validateSerialNumber.initiate({ serialNumber: value })
                        );
                        if (latestSerialNumber !== value) return true;
                        return result.data?.is_valid_serial_number ?? false;
                    } catch (error) {
                        return latestSerialNumber !== value;
                    }
                }
            ),
        documents: Yup.array()
            .of(
                Yup.object({
                    file: Yup.mixed<File>()
                        .required(
                            formatMessage(
                                { id: 'schema.errors.required', defaultMessage: '{field} is required' },
                                { field: formatMessage({ id: 'common.file', defaultMessage: 'File' }) }
                            )
                        )
                        .test(
                            'fileSize',
                            formatMessage(
                                { id: 'schema.errors.fileSize', defaultMessage: 'File is bigger than {size}' },
                                { size: '10MB' }
                            ),
                            (value) => {
                                if (!value) return true; // Skip validation if no file is provided
                                const maxSizeInBytes = 10 * 1024 * 1024; // 10MB
                                return value.size <= maxSizeInBytes;
                            }
                        ),
                    isLoading: Yup.boolean(),
                    error: Yup.string().nullable(),
                })
            )
            .min(
                1,
                formatMessage(
                    {
                        id: 'schema.errors.array.min',
                        defaultMessage: 'At least {min} {field} is required',
                    },
                    {
                        min: 1,
                        field: formatMessage({ id: 'common.documents', defaultMessage: 'Documents' }),
                    }
                )
            )
            .required(
                formatMessage(
                    { id: 'schema.errors.required', defaultMessage: '{field} is required' },
                    { field: formatMessage({ id: 'common.documents', defaultMessage: 'Documents' }) }
                )
            ),
        comment: Yup.string().trim().nullable(),
        confirm_truth: Yup.boolean().oneOf(
            [true],
            formatMessage({
                id: 'rla.errors.truth',
                defaultMessage: 'You must confirm the truthfulness of the information provided',
            })
        ),
        confirm_intent: Yup.boolean().oneOf(
            [true],
            formatMessage({
                id: 'rla.errors.intent',
                defaultMessage: 'You must understand the intent of this information',
            })
        ),
    });
